/**
 * ContractorBidPreviewModal
 * -------------------------
 * Read-only "what the contractor sees" preview of a bid request. Opened from
 * the Bid Requests / Deal Workspace card so the owner can confirm exactly how
 * their scope, builder branding and attachments present to an external
 * contractor before (and after) it's sent.
 *
 * It faithfully mirrors the contractor portal's bid-detail layout
 * (see pages/ContractorBidPortal.js) but every input/action is disabled — this
 * is a presentation preview, not an interactive portal.
 *
 * Props:
 *   open           — boolean
 *   onClose        — () => void
 *   bidRequestId   — id of the bid request to preview
 *   userId         — owner id (getBidRequest is ownership-scoped)
 */
import React, { useEffect, useState } from 'react';
import {
  Gavel, Building2, Lock, FileText, Download, Calendar, MessageSquare,
  Plus, Loader2, AlertCircle, Eye,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiService } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

const getDaysRemaining = (dueDate) => {
  if (!dueDate) return 0;
  const diff = new Date(dueDate) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export default function ContractorBidPreviewModal({ open, onClose, bidRequestId, userId }) {
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !bidRequestId || !userId) return;
    setLoading(true);
    setError(null);
    setBid(null);
    (async () => {
      try {
        const res = await apiService.getBidRequest(bidRequestId, userId);
        if (!cancelled) setBid(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Unable to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, bidRequestId, userId]);

  const project = bid?.project || {};
  const documents = bid?.documents || [];
  const daysRemaining = getDaysRemaining(bid?.due_date);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="contractor-bid-preview-modal"
      >
        <DialogTitle className="sr-only">Contractor bid request preview</DialogTitle>
        <DialogDescription className="sr-only">
          A read-only preview of how this bid request appears to the contractor who receives it.
        </DialogDescription>
        {/* Preview ribbon */}
        <div className="bg-amber-500 text-white text-xs font-semibold px-4 py-2 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          Preview — this is exactly what your contractor sees. Nothing here is editable.
        </div>

        {/* Contractor portal header (mirrors ContractorBidPortal) */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white py-5 px-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">DrawReady</h1>
              <Badge className="bg-orange-500/20 text-orange-200 border-orange-400/30">Contractor Portal</Badge>
            </div>
          </div>
        </div>

        <div className="p-5">
          {loading && (
            <div className="py-16 text-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
              <p className="text-sm text-slate-500 mt-3">Loading preview…</p>
            </div>
          )}

          {!loading && error && (
            <div className="py-12 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="text-slate-600">{error}</p>
            </div>
          )}

          {!loading && !error && bid && (
            <div className="space-y-5" data-testid="preview-bid-detail">
              {/* Title row */}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <Badge className="mb-2 bg-blue-100 text-blue-700">BID REQUEST</Badge>
                  {/* Builder branding — matches contractor portal card */}
                  {project.builder_company_name && (
                    <div className="flex items-center gap-2 mb-2" data-testid="preview-builder">
                      {project.builder_company_logo ? (
                        <img
                          src={project.builder_company_logo}
                          alt=""
                          className="w-6 h-6 rounded object-contain bg-white border border-slate-200"
                        />
                      ) : (
                        <span className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        </span>
                      )}
                      <span className="text-xs text-slate-500">
                        Bidding for <span className="font-bold text-slate-800">{project.builder_company_name}</span>
                      </span>
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-slate-900">{bid.title}</h2>
                  <p className="text-sm text-slate-500">
                    {project.name}{project.address ? ` • ${project.address}` : ''}
                  </p>
                  {bid.trade_type && (
                    <Badge variant="outline" className="mt-2">{bid.trade_type}</Badge>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="text-lg font-bold text-red-600">{formatDate(bid.due_date)}</p>
                  <p className="text-sm text-slate-500">{daysRemaining} days remaining</p>
                </div>
              </div>

              {/* Scope of Work */}
              <div>
                <h4 className="font-medium mb-2">Scope of Work</h4>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="whitespace-pre-wrap text-slate-700">
                    {bid.scope_of_work || 'No scope provided.'}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              {documents.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Attachments</h4>
                  <div className="flex flex-wrap gap-2">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="inline-flex items-center gap-2 text-sm border border-slate-200 rounded-md px-3 py-1.5 text-slate-600 bg-white"
                      >
                        <FileText className="w-4 h-4" />
                        {doc.file_name}
                        <Download className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Questions section (read-only mirror) */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Have a question about this scope?
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  The contractor can message you directly from here before submitting.
                </p>
              </div>

              {/* Response form mirror (disabled) */}
              <div className="border border-slate-200 rounded-lg p-4 opacity-90">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <h4 className="font-medium">Your Bid</h4>
                </div>
                <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">
                  <Plus className="w-4 h-4 inline mr-1" />
                  The contractor enters line items, pricing and a timeline here.
                </div>
                <Button disabled className="mt-4 w-full" data-testid="preview-submit-disabled">
                  <Lock className="w-4 h-4 mr-2" /> Submit Bid (disabled in preview)
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="preview-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
